import type { GDrive_FileID, GDrive_FolderID, GoogleWorkspaceRemote, MimeType } from '@imago/api-client-google-workspace'
import { DocsV1_Documents_batchUpdate, DocsV1_Documents_get, DriveV3A_Files_create, DriveV3A_Files_get, DriveV3A_Files_getMedia, DriveV3A_Files_list, DriveV3A_Files_update } from '@imago/api-client-google-workspace'
import { docs_v1, drive_v3 } from 'googleapis'
import { type DocBlock, type DocBlockClass } from '../documents/_Doc.ts'

export interface SimpleDriveRemote {
	google: GoogleWorkspaceRemote
	driveID: string
	tmpFolderID: string
}

export interface SimpleDriveFile {
	id: GDrive_FileID
	parents: {
		[0]: GDrive_FolderID
	}
	name: string
	mimeType: MimeType
}

export interface SimpleDriveFileWithCreatedTime extends SimpleDriveFile {
	createdTime: string
}

export async function SimpleDriveV1_Files_get(remote: SimpleDriveRemote, fileID: GDrive_FileID): Promise<SimpleDriveFile> {
	const file = await DriveV3A_Files_get(remote.google, fileID, {
		fields: 'name,parents,mimeType'
	})
	return {
		...file,
		id: fileID,
		parents: [file.parents![0]! as GDrive_FolderID],
		name: file.name!,
		mimeType: file.mimeType! as MimeType,
	}
}

async function _SimpleDriveV1_Files_list(remote: SimpleDriveRemote, folderID: GDrive_FolderID, additionalFields: string): Promise<SimpleDriveFile[]> {
	let pageToken = ''
	let files = new Array<drive_v3.Schema$File>()
	do {
		const resp = await DriveV3A_Files_list(remote.google, {
			includeItemsFromAllDrives: true,
			corpora: 'drive',
			driveId: remote.driveID,
			pageSize: 1000,
			q: `trashed = false and ${query_quote(folderID)} in parents`,
			fields: `files(id,name,mimeType${additionalFields}),incompleteSearch,nextPageToken`,
			pageToken,
		})
		if (resp.incompleteSearch) {
			console.warn('Incomplete search!')
		}
		pageToken = resp.nextPageToken!
		files.push(...resp.files!)
		console.debug(files.length)
	} while (pageToken)
	return files.map(file => ({
		...file,
		id: file.id! as GDrive_FileID,
		parents: [folderID],
		name: file.name!,
		mimeType: file.mimeType! as MimeType,
	}))
}

export async function SimpleDriveV1_Files_list(remote: SimpleDriveRemote, folderID: GDrive_FolderID): Promise<SimpleDriveFile[]> {
	return await _SimpleDriveV1_Files_list(remote, folderID, '')
}

export async function SimpleDriveV1_Files_listWithCreatedTime(remote: SimpleDriveRemote, folderID: GDrive_FolderID): Promise<SimpleDriveFileWithCreatedTime[]> {
	return await _SimpleDriveV1_Files_list(remote, folderID, ',createdTime') as SimpleDriveFileWithCreatedTime[]
}

export async function SimpleDriveV1_Files_createEmpty(remote: SimpleDriveRemote, folderID: GDrive_FolderID, name: string, properties?: Record<string, string>): Promise<SimpleDriveFile> {
	const file = await DriveV3A_Files_create(remote.google, {
		name,
		parents: [folderID],
		mimeType: 'application/octet-stream',
		...(properties ? {properties} : {}),
	}, {
		fields: 'id,name,mimeType',
	})
	return {
		id: file.id! as GDrive_FileID,
		parents: [folderID],
		name: file.name!,
		mimeType: file.mimeType! as MimeType,
	}
}

export async function SimpleDriveV1_Files_createAndSetContent(remote: SimpleDriveRemote, folderID: GDrive_FolderID, name: string, content: Blob): Promise<SimpleDriveFile> {
	const file = await DriveV3A_Files_create(remote.google, {
		name,
		parents: [folderID],
		media: content,
	}, {
		fields: 'id,name,mimeType',
	})
	return {
		id: file.id! as GDrive_FileID,
		parents: [folderID],
		name: file.name!,
		mimeType: file.mimeType! as MimeType,
	}
}

export async function SimpleDriveV1_Files_rename(remote: SimpleDriveRemote, ID: GDrive_FileID, name: string): Promise<SimpleDriveFile> {
	const file = await DriveV3A_Files_update(remote.google, ID, {
		name
	}, {
		fields: 'id,parents,name,mimeType',
	})
	return {
		id: file.id! as GDrive_FileID,
		parents: [file.parents![0]! as GDrive_FolderID],
		name: file.name!,
		mimeType: file.mimeType! as MimeType,
	}
}

export async function SimpleDriveV1_Files_delete(remote: SimpleDriveRemote, ID: GDrive_FileID): Promise<SimpleDriveFile> {
	const file = await DriveV3A_Files_update(remote.google, ID, {
		trashed: true,
	}, {
		fields: 'id,parents,name,mimeType',
	})
	return {
		id: file.id! as GDrive_FileID,
		parents: [file.parents![0]! as GDrive_FolderID],
		name: file.name!,
		mimeType: file.mimeType! as MimeType,
	}
}

export async function SimpleDriveV1_Folders_createAndSetContent(remote: SimpleDriveRemote, folderID: GDrive_FolderID, name: string, content: Map<string, Blob>): Promise<SimpleDriveFile> {
	const file = await DriveV3A_Files_create(remote.google, {
		name,
		parents: [remote.tmpFolderID],
		mimeType: 'application/vnd.google-apps.folder',
	}, {
		fields: 'id',
	})
	const ID = file.id! as GDrive_FolderID
	await SimpleDriveV1_Folders_setContent(remote, ID, content)
	await DriveV3A_Files_update(remote.google, ID, {}, {
		addParents: folderID,
		removeParents: remote.tmpFolderID,
		fields: '',
	})
	return {
		id: ID,
		parents: [folderID],
		name: name,
		mimeType: 'application/vnd.google-apps.folder' as MimeType,
	}
}

export async function SimpleDriveV1_Folders_createEmpty(remote: SimpleDriveRemote, folderID: GDrive_FolderID, name: string): Promise<SimpleDriveFile> {
	const file = await DriveV3A_Files_create(remote.google, {
		name,
		parents: [folderID],
		mimeType: 'application/vnd.google-apps.folder',
	}, {
		fields: 'id',
	})
	const ID = file.id! as GDrive_FolderID
	return {
		id: ID,
		parents: [folderID],
		name: name,
		mimeType: 'application/vnd.google-apps.folder' as MimeType,
	}
}

export async function SimpleDriveV1_Folders_getContent(remote: SimpleDriveRemote, ID: GDrive_FolderID): Promise<Map<string, Blob>> {
	const files = await SimpleDriveV1_Files_list(remote, ID)
	const contents = await Promise.all(files.map(async file => [file.name, await DriveV3A_Files_getMedia(remote.google, file.id)] satisfies [string, Blob]))
	return new Map(contents)
}

export async function SimpleDriveV1_Folders_setContent(remote: SimpleDriveRemote, ID: GDrive_FolderID, files: Map<string, Blob>): Promise<void> {
	await Promise.all([...files].map(async ([name, blob]) => await DriveV3A_Files_create(remote.google, {
		name,
		parents: [ID],
		media: blob,
	}, {
		fields: '',
	})))
}

export async function SimpleDriveV1_Documents_createAndSetContent(remote: SimpleDriveRemote, folderID: GDrive_FolderID, name: string, content: DocBlock[]): Promise<SimpleDriveFile> {
	const file = await DriveV3A_Files_create(remote.google, {
		name,
		parents: [remote.tmpFolderID],
		mimeType: 'application/vnd.google-apps.document',
	}, {
		fields: 'id',
	})
	const ID = file.id! as GDrive_FileID
	await SimpleDriveV1_Documents_setContent(remote, ID, content)
	await DriveV3A_Files_update(remote.google, ID, {}, {
		addParents: folderID,
		removeParents: remote.tmpFolderID,
		fields: '',
	})
	return {
		id: ID,
		parents: [folderID],
		name: name,
		mimeType: 'application/vnd.google-apps.document' as MimeType,
	}
}

export async function SimpleDriveV1_Documents_createEmpty(remote: SimpleDriveRemote, folderID: GDrive_FolderID, name: string): Promise<SimpleDriveFile> {
	const file = await DriveV3A_Files_create(remote.google, {
		name,
		parents: [folderID],
		mimeType: 'application/vnd.google-apps.document',
	}, {
		fields: 'id',
	})
	const ID = file.id! as GDrive_FileID
	return {
		id: ID,
		parents: [folderID],
		name: name,
		mimeType: 'application/vnd.google-apps.document' as MimeType,
	}
}

export async function SimpleDriveV1_Documents_getContent(remote: SimpleDriveRemote, ID: GDrive_FileID): Promise<DocBlock[]> {
	const doc = await DocsV1_Documents_get(remote.google, ID, {
		fields: 'body'
	})
	const body = doc.body!
	return body.content?.map(x => x.paragraph).filter(para => para !== undefined).map(para => {
		const textWithNewline = para.elements?.map(y => y.textRun?.content).filter(y => y).join('') || '\n'
		if (textWithNewline[textWithNewline.length - 1] != '\n') throw new Error('Newline expected')
		const text = textWithNewline.substring(0, textWithNewline.length - 1)
		return {
			class: para.paragraphStyle?.namedStyleType as DocBlockClass ?? 'NORMAL_TEXT',
			text: text.replaceAll('\v', '\n'),
		} satisfies DocBlock
	}) ?? []
}

export async function SimpleDriveV1_Documents_setContent(remote: SimpleDriveRemote, ID: GDrive_FileID, blocks: DocBlock[]): Promise<void> {
	const doc = await DocsV1_Documents_get(remote.google, ID, {
		fields: 'body'
	})
	const body = doc.body!

	const endIndex = (body.content?.pop()?.endIndex || 1) - 1

	const vBlocks = blocks.map(block => {return {class: block.class, text: block.text.replaceAll('\n', '\v').replaceAll('\r', '')}})

	const requests = Array<docs_v1.Schema$Request>()

	if (endIndex > 1)
		requests.push({deleteContentRange: {range: {startIndex: 1, endIndex}}})

	if (vBlocks.length) {
		const insertText = {text: '', endOfSegmentLocation: {}}
		requests.push({insertText})

		for (const line of vBlocks) {
			requests.push({updateParagraphStyle: {
				paragraphStyle: {namedStyleType: line.class},
				fields: 'namedStyleType',
				range: {startIndex: insertText.text.length + 1, endIndex: insertText.text.length + 2},
			}})
			insertText.text += line.text + '\n'
		}
		insertText.text = insertText.text.substring(0, insertText.text.length - 1)
	}

	if (requests.length) {
		await DocsV1_Documents_batchUpdate(remote.google, ID, {
			requests
		})
	}
}

export function query_quote(text: string) {
	return `'${text.replaceAll('\\', '\\\\').replaceAll('\'', '\\\'')}'`
}
